import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { useScheduleBoardModals } from 'af-root/hooks/useScheduleBoardModal';

import type { ScheduleBoardOnDateViewModel } from 'ab-viewModels/scheduleBoardWorkOrdersOnDateView.viewModel';

import type { EmployeeVM } from 'ab-socketModels/viewModels/scheduleBoard/scheduleBoardWorkOrder.viewModel';

import type { NotificationStatusEnum } from '@acceligentllc/shared/enums/notificationStatus';
import { ScheduleBoardContext } from '@acceligentllc/shared/enums/scheduleBoardContext';
import { ScheduleBoardProperty } from '@acceligentllc/shared/enums/scheduleBoardProperty';
import { WorkOrderStatus } from '@acceligentllc/shared/enums/workOrderStatus';

import type { RootState } from 'af-reducers';

import * as ScheduleBoardUtil from 'af-utils/scheduleBoard.util';

import { Button } from '@acceligentllc/storybook';

import OrderInfo from '../OrderInfo';
import ResourceDroppable from '../ResourceDroppable';

interface OwnProps {
	// Card OwnProps
	/** `MM-DD-YYYY` */
	dueDate: string;
	hasPermissionsToEditScheduleBoard: boolean;
	ignorePlaceholders?: boolean;
	isCalculationsView?: boolean;
	isDragAndDropDisabled: boolean;
	maxResourceItems: number;
	workOrderCode: string;
	/** prop not used if `isCalculationsView = true` */
	lastOpenedOrderCode?: string;
	// Card StateProps
	isCardDisabled: boolean;
	notificationStatus: Nullable<NotificationStatusEnum>;
	// CardData specific props
	isCopyPlaceholder?: boolean;
	isDragging: boolean;
	shouldRoundTimeIntervalsTo1Min?: boolean;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const _getEmployeeLabel = (employee: Nullable<EmployeeVM>, isDuplicate: boolean) => {
	if (!employee) {
		return 'N/A';
	}
	const { firstName, lastName } = employee;
	return `${firstName[0]}${isDuplicate ? firstName[1] : ''} ${lastName}`;
};

const CardData: React.FC<Props> = (props) => {
	const {
		dueDate,
		hasPermissionsToEditScheduleBoard,
		hasProjectManagerDuplicateLastName,
		hasSuperintendentDuplicateLastName,
		ignorePlaceholders,
		isCalculationsView,
		isCardDisabled,
		isCopyPlaceholder = false,
		isDragAndDropDisabled,
		isDragging,
		isShowNotesActive,
		isShowAddressNotesActive,
		lastOpenedOrderCode,
		maxResourceItems,
		notificationStatus,
		resourceIds = [],
		workOrder,
		workOrderCode,
		projectCode,
		shouldRoundTimeIntervalsTo1Min,
	} = props;

	const { setWorkOrderNoteModalData } = useScheduleBoardModals();

	const openWorkOrderNoteModal = React.useCallback(() => {
		if (
			workOrder &&
			workOrder.status !== WorkOrderStatus.LOCKED
			&& !workOrder.isPaused
			&& workOrder.status !== WorkOrderStatus.CANCELED
		) {
			setWorkOrderNoteModalData(workOrder.code, workOrder.dueDate);
		}
	}, [setWorkOrderNoteModalData, workOrder]);

	const workOrderAddressNotes = React.useMemo(() => {
		if (!workOrder.workLocations?.length) {
			return null;
		}
		const { formattedFullStreet, notes } = workOrder.workLocations[0];
		if (formattedFullStreet && notes) {
			return formattedFullStreet + ', ' + notes;
		}
		return formattedFullStreet
			? formattedFullStreet
			: notes;
	}, [workOrder.workLocations]);

	if (!workOrder) {
		return null;
	}

	const supervisorName = _getEmployeeLabel(workOrder.supervisor, hasSuperintendentDuplicateLastName);
	const projectManagerName = _getEmployeeLabel(workOrder.projectManager, hasProjectManagerDuplicateLastName);
	const shouldShowNotes = isShowNotesActive && !isCalculationsView;
	const shouldShowAddressNotes = isShowAddressNotesActive && !isCalculationsView;
	const isWorkOrderCanceled: boolean = workOrder.status === WorkOrderStatus.CANCELED;
	const droppableId = ScheduleBoardUtil.generateDroppableId(ScheduleBoardContext.BOARD, ScheduleBoardProperty.RESOURCE, dueDate, workOrder.code);

	const isResourcesContainerReadOnly = isDragAndDropDisabled || !hasPermissionsToEditScheduleBoard;
	const resourcesContainerClassName = `schedule-board-card-resources ${isResourcesContainerReadOnly ? `items-${maxResourceItems}` : ''}`;
	const dragAndDropDisabled = isDragAndDropDisabled || workOrder.isPaused;

	const className = projectCode && process.env.FTD_PROJECT !== 'true' ? 'project' : '';

	return (
		<div className={className}>
			<OrderInfo
				dueDate={dueDate}
				isCalculationsView={!!isCalculationsView}
				isDragging={isDragging}
				lastOpenedOrderCode={lastOpenedOrderCode}
				notificationStatus={notificationStatus}
				projectManagerName={projectManagerName}
				shouldRoundTimeIntervalsTo1Min={shouldRoundTimeIntervalsTo1Min ?? false}
				supervisorName={supervisorName}
				workOrder={workOrder}
			/>
			<div className={resourcesContainerClassName}>
				<ResourceDroppable
					draggablePrefix={ScheduleBoardContext.BOARD}
					droppableId={droppableId}
					dueDate={dueDate}
					hasPermissionsToEditScheduleBoard={hasPermissionsToEditScheduleBoard}
					ignorePlaceholders={!!ignorePlaceholders}
					isCalculationsView={!!isCalculationsView}
					isCardDisabled={isCardDisabled}
					isCopyPlaceholder={isCopyPlaceholder}
					isDragAndDropDisabled={dragAndDropDisabled}
					isWOLocked={workOrder.status === WorkOrderStatus.LOCKED}
					isWorkOrderCanceled={isWorkOrderCanceled}
					resourceIds={resourceIds}
					workOrderCode={workOrderCode}
					wrappedColumns={true}
				/>
			</div>
			{(shouldShowAddressNotes && workOrderAddressNotes) &&
				<div className="schedule-board-card-notes">
					{workOrderAddressNotes ?? '-'}
				</div>
			}
			{shouldShowNotes &&
				<div className="schedule-board-card-notes" onClick={!!workOrder.workOrderNotes ? openWorkOrderNoteModal : undefined}>
					{
						!!workOrder.workOrderNotes ?
							(workOrder.workOrderNotes) :
							<Button
								fullWidth={true}
								icon="ActionPlusSmall"
								label="Add a Note"
								onClick={openWorkOrderNoteModal}
								size="small"
								style="link"
							/>
					}
				</div>
			}
		</div>
	);
};

function mapStateToProps(state: RootState, ownProps: OwnProps) {
	const { dueDate, workOrderCode } = ownProps;
	const {
		employees,
		isShowNotesActive,
		isShowAddressNotesActive,
		workOrdersByDateDictionary,
	} = state.scheduleBoard;
	const workOrdersOnDateDict: ScheduleBoardOnDateViewModel = workOrdersByDateDictionary[dueDate];
	const workOrder = workOrdersOnDateDict?.workOrders?.[workOrderCode] ?? null;
	const employeesDict = employees ?? {};

	return {
		hasProjectManagerDuplicateLastName: !!(workOrder?.projectManagerId && employeesDict[workOrder?.projectManagerId]?.duplicateDisplayNameExists) || false,
		hasSuperintendentDuplicateLastName: !!(workOrder?.supervisorId && employeesDict[workOrder?.supervisorId]?.duplicateDisplayNameExists) || false,
		isShowNotesActive,
		isShowAddressNotesActive,
		resourceIds: workOrder?.workOrderResourceLookups ?? undefined,
		workOrder,
		projectCode: workOrder?.projectJobCode ?? null,
	};
}

const connector = connect(mapStateToProps);

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(CardData);
